import {
  run as spellCheckEmail,
  POPULAR_DOMAINS,
} from "@zootools/email-spell-checker";

// "Did you mean ...?" typo suggestions for the auth email field. Catches the
// case psl can't: a mistyped domain NAME with a real TLD (e.g. "gmial.com",
// where ".com" is valid so the structure + TLD checks both pass).
//
// SUGGESTION ONLY — it never blocks. Real validity stays with
// isValidEmailStructure (shape) + the server-side psl TLD check (email-domain).
// Pure client-side, in-memory, no network — safe to call on every blur.
//
// PRECISION over recall: in an auth flow a WRONG suggestion is worse than none.
// Left to itself the library produces confusing corrections — e.g. "gmail.in"
// -> "mail.in", "gmial.cob" -> "gmial.co", or hops between unrelated real ISPs
// ("cox.net" -> "gmx.net"). Two lists keep it honest:

// SUGGESTABLE_DOMAINS — the only domains we will ever suggest. A curated list of
// major consumer webmail providers, used BOTH as the library's full-domain match
// list (so TLD typos like "yahoo.con" -> "yahoo.com" are caught) and as the
// output guard (so a correction can never land on a nonsense/unrelated domain).
// Deliberately excludes regional US-ISP domains (they cause cross-brand hops)
// and any "mail.*" ("mail" collides with "gmail").
const SUGGESTABLE_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "yahoo.in",
  "yahoo.ca",
  "yahoo.com.au",
  "ymail.com",
  "rocketmail.com",
  "hotmail.com",
  "hotmail.co.uk",
  "hotmail.fr",
  "hotmail.it",
  "hotmail.es",
  "outlook.com",
  "outlook.co.uk",
  "live.com",
  "live.co.uk",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "zoho.com",
  "yandex.com",
  "yandex.ru",
  "gmx.com",
  "gmx.net",
  "gmx.de",
  "web.de",
  "qq.com",
  "163.com",
  "126.com",
];
const SUGGESTABLE_SET = new Set(SUGGESTABLE_DOMAINS);

// RECOGNISED_DOMAINS — domains we treat as already-valid and therefore never
// second-guess. Broader than SUGGESTABLE_DOMAINS (it also includes the library's
// full provider + ISP list), so a real address like "cox.net" or "comcast.net"
// never gets a spurious "did you mean" hint.
const RECOGNISED_DOMAINS = new Set([
  ...POPULAR_DOMAINS,
  ...SUGGESTABLE_DOMAINS,
]);

/**
 * Returns a corrected full email (e.g. "user@gmail.com") when the input looks
 * like a typo of a recognised provider, or null when it looks fine, has no
 * close match, or would only correct to a domain we don't trust.
 */
export function getEmailSuggestion(email: string): string | null {
  const trimmed = email.trim();
  if (!trimmed) return null;
  const at = trimmed.lastIndexOf("@");
  // Don't second-guess a domain the user already typed and we recognise.
  if (at > 0 && RECOGNISED_DOMAINS.has(trimmed.slice(at + 1).toLowerCase())) {
    return null;
  }
  const suggestion = spellCheckEmail({
    email: trimmed,
    domains: SUGGESTABLE_DOMAINS,
  });
  // Only surface a correction to a curated target, so we never show a nonsense
  // suggestion like "gmail.in" -> "mail.in".
  if (!suggestion || !SUGGESTABLE_SET.has(suggestion.domain)) return null;
  // The library lowercases the WHOLE address. Local parts can be
  // case-sensitive, so preserve the user's original local-part casing and only
  // swap in the corrected (case-insensitive) domain.
  if (at > 0) {
    return `${trimmed.slice(0, at)}@${suggestion.domain}`;
  }
  return suggestion.full;
}
